import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { enUS, fr as frLocale } from "date-fns/locale";
import type { Locale } from "date-fns";

import en, { type TranslationKey } from "./en";
import fr from "./fr";

export type Lang = "en" | "fr";

const dictionaries: Record<Lang, Record<TranslationKey, string>> = { en, fr };
const dateLocales: Record<Lang, Locale> = { en: enUS, fr: frLocale };

const STORAGE_KEY = "app_lang";

const detectLang = (): Lang => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "fr") return stored;
  return "en";
};

type TranslateVars = Record<string, string | number>;

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey, vars?: TranslateVars) => string;
  dateLocale: Locale;
}

const I18nContext = createContext<I18nValue | null>(null);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(detectLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: TranslateVars) => {
      let text = dictionaries[lang][key] ?? en[key] ?? String(key);
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.split(`{${name}}`).join(String(value));
        }
      }
      return text;
    },
    [lang],
  );

  const value = useMemo<I18nValue>(
    () => ({ lang, setLang, t, dateLocale: dateLocales[lang] }),
    [lang, setLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback so components rendered outside the provider still work.
    return {
      lang: "en",
      setLang: () => undefined,
      t: (key: TranslationKey) => en[key] ?? String(key),
      dateLocale: enUS,
    };
  }
  return ctx;
};
